/**
 * Voice Test Panel
 *
 * Debug panel for testing ElevenLabs TTS and STT integration.
 * Allows testing voices, intonations, and full narrative playback.
 */

import { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Slider,
  Divider,
} from "@mui/material";
import {
  PlayArrow,
  Stop,
  Mic,
  ContentCopy,
  VolumeUp,
  VolumeOff,
  Delete,
} from "@mui/icons-material";
import { AudioPlayer } from "../../lib/voice/audio-player";
import { RealtimeSpeechRecorder } from "../../lib/voice/speech-recorder";
import { VOICES, NPC_TYPE_VOICES } from "../../lib/voice/voices";
import { INTONATION_TAGS } from "../../lib/voice/intonation-tags";

interface VoiceTestPanelProps {
  proxyUrl: string;
}

interface DebugLog {
  timestamp: string;
  type: "request" | "response" | "error" | "info";
  message: string;
  data?: any;
}

export function VoiceTestPanel({ proxyUrl }: VoiceTestPanelProps) {
  const [tab, setTab] = useState(0);

  // TTS state
  const [ttsText, setTtsText] = useState(
    "[NARRATOR]The Dubious Intent screams through space.\n\n[COMPUTER][SARCASM]\"Hull breach detected. This is fine.\"[/SARCASM][NARRATOR]\n\n[NPC:pirate][THREATENING]\"You're not getting away.\"[/THREATENING][NARRATOR]"
  );
  const [selectedVoice, setSelectedVoice] = useState("narrator");
  const [selectedIntonation, setSelectedIntonation] = useState("none");
  const [ttsStatus, setTtsStatus] = useState("");
  const [playbackSpeed, setPlaybackSpeed] = useState(1.15);
  const [muted, setMuted] = useState(false);

  // STT state
  const [sttStatus, setSttStatus] = useState("");
  const [transcript, setTranscript] = useState("");
  const [finalTranscript, setFinalTranscript] = useState("");
  const [volume, setVolume] = useState(0);
  const [isRecording, setIsRecording] = useState(false);

  // Debug logs
  const [debugLogs, setDebugLogs] = useState<DebugLog[]>([]);
  const debugBoxRef = useRef<HTMLDivElement>(null);

  // Refs
  const playerRef = useRef<AudioPlayer | null>(null);
  const recorderRef = useRef<RealtimeSpeechRecorder | null>(null);

  const addLog = (type: DebugLog["type"], message: string, data?: any) => {
    const log: DebugLog = {
      timestamp: new Date().toLocaleTimeString(),
      type,
      message,
      data,
    };
    setDebugLogs((prev) => [...prev, log]);

    // Auto-scroll to bottom
    setTimeout(() => {
      if (debugBoxRef.current) {
        debugBoxRef.current.scrollTop = debugBoxRef.current.scrollHeight;
      }
    }, 100);
  };

  // Initialize
  useEffect(() => {
    addLog("info", `Proxy URL: ${proxyUrl}`);

    playerRef.current = new AudioPlayer(proxyUrl);
    playerRef.current.setEventHandlers({
      onStateChange: (state) => {
        setTtsStatus(`Playback: ${state}`);
        addLog("info", `TTS playback state: ${state}`);
      },
      onProgress: (cur, total) => {
        setTtsStatus(`Playing: ${cur + 1}/${total}`);
      },
      onComplete: () => {
        setTtsStatus("Playback complete");
        addLog("info", "TTS playback complete");
      },
      onDebugLog: addLog,
    });
    playerRef.current.setSpeed(playbackSpeed);

    recorderRef.current = new RealtimeSpeechRecorder(proxyUrl);
    recorderRef.current.setEventHandlers({
      onStateChange: (state) => {
        setSttStatus(`Recording: ${state}`);
        setIsRecording(state === "recording" || state === "ready");
        addLog("info", `STT state: ${state}`);
      },
      onPartialTranscript: (text) => {
        setTranscript(text);
        addLog("info", `Partial transcript: ${text}`);
      },
      onFinalTranscript: (text) => {
        setFinalTranscript(text);
        setTranscript("");
        addLog("info", `Final transcript: ${text}`);
      },
      onVolumeLevel: setVolume,
      onError: (error) => {
        setSttStatus(`Error: ${error.message}`);
        addLog("error", `STT error: ${error.message}`, error);
      },
      onDebugLog: addLog,
    });

    return () => {
      playerRef.current?.dispose();
      recorderRef.current?.cancel();
    };
  }, [proxyUrl, playbackSpeed]);

  // TTS handlers
  const handlePlayTts = async () => {
    if (!playerRef.current) return;
    try {
      setTtsStatus("Loading audio...");
      addLog("request", "TTS: Loading narrative", { text: ttsText });
      await playerRef.current.load(ttsText);
      addLog("response", "TTS: Audio loaded");
      await playerRef.current.play();
    } catch (error: any) {
      addLog("error", "TTS playback failed", error);
    }
  };

  const handleStopTts = () => {
    playerRef.current?.stop();
    setTtsStatus("Stopped");
    addLog("info", "TTS stopped");
  };

  const handleTestVoice = async () => {
    const voiceTag = selectedVoice === "narrator" ? "[NARRATOR]" : `[NPC:${Object.keys(NPC_TYPE_VOICES).find(k => NPC_TYPE_VOICES[k] === selectedVoice) || "trader"}]`;
    const intonationOpen = selectedIntonation !== "none" ? `[${selectedIntonation.toUpperCase()}]` : "";
    const intonationClose = selectedIntonation !== "none" ? `[/${selectedIntonation.toUpperCase()}]` : "";

    const testText = `${voiceTag}${intonationOpen}This is a test of the ${VOICES[selectedVoice]?.name} voice.${intonationClose}[NARRATOR]`;

    if (!playerRef.current) return;
    try {
      setTtsStatus("Loading test audio...");
      addLog("request", "TTS: Testing voice", { voice: selectedVoice, intonation: selectedIntonation, text: testText });
      await playerRef.current.load(testText);
      addLog("response", "TTS: Test audio loaded");
      await playerRef.current.play();
    } catch (error: any) {
      addLog("error", "TTS test failed", error);
    }
  };

  // STT handlers
  const handleToggleRecording = async () => {
    const state = recorderRef.current?.getState();
    addLog("info", `STT: Toggle recording (current state: ${state})`);

    if (state === "idle" || state === "error") {
      try {
        addLog("request", "STT: Starting recording");
        await recorderRef.current?.start();
      } catch (error: any) {
        addLog("error", "STT start failed", error);
      }
    } else if (state === "recording" || state === "ready") {
      try {
        addLog("request", "STT: Stopping recording");
        await recorderRef.current?.stop();
      } catch (error: any) {
        addLog("error", "STT stop failed", error);
      }
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addLog("info", `Copied to clipboard: ${text}`);
  };

  const clearLogs = () => {
    setDebugLogs([]);
  };

  // Sample narratives
  const sampleNarratives = [
    {
      name: "Combat Escape",
      text: "[SFX:alert]\n\n[PANIC]THUNK. Pulse laser fire punches through your shields.[/PANIC] The Dubious Intent screams through the asteroid field.\n\n[COMPUTER][DEADPAN]\"Hull breach on deck three. Shall I add 'structural integrity' to your list of concerns?\"[/DEADPAN][NARRATOR]\n\n[SFX:dock]\n\nSpindle Central Market's docking clamps grab your ship like a drowning man clutching a life preserver.\n\n[NPC:pirate][THREATENING]\"See you around, Dubious Intent. We'll remember that cargo signature.\"[/THREATENING][NARRATOR]\n\n[PA][DEADPAN]\"WELCOME TO SPINDLE CENTRAL - 40% More Genuine Than Before!\"[/DEADPAN][NARRATOR]",
    },
    {
      name: "Trading Success",
      text: "[SFX:trade]\n\n[CHEERFUL]The trade completes with a satisfying chime.[/CHEERFUL]\n\n[NPC:merchant]\"Pleasure doing business. Those Antarian spices will fetch a pretty credit in the core systems.\"[NARRATOR]\n\n[COMPUTER][SMUG]\"Cargo secured. Your profit margin is... surprisingly not terrible.\"[/SMUG][NARRATOR]\n\nYour credit balance ticks upward. Finally, something went right.",
    },
    {
      name: "Station Bureaucracy",
      text: "[PA][DEADPAN]\"Attention all vessels. Due to unforeseen circumstances, docking bay 7 is now docking bay 12. Docking bay 12 is now a coffee shop. Please update your navigation accordingly.\"[/DEADPAN][NARRATOR]\n\n[NPC:official][DEADPAN]\"Form 27B stroke 6. In triplicate.\"[/DEADPAN][NARRATOR]\n\nYou stare at the form. The form stares back. You're fairly certain the form is winning.\n\n[COMPUTER][SARCASM]\"I've highlighted the sections that don't make sense. All of them.\"[/SARCASM][NARRATOR]",
    },
  ];

  return (
    <Card
      className="panel"
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      {/* Fixed Header with Tabs */}
      <CardContent className="panel-header" sx={{ p: 1.5, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'info.main',
              fontWeight: 700,
              letterSpacing: 1,
              fontSize: '0.65rem',
            }}
          >
            🎙️ Voice Test Panel
          </Typography>
          <Chip
            label={proxyUrl}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.6rem', fontFamily: 'monospace' }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Test ElevenLabs TTS and STT integration
        </Typography>

        {/* Tabs in header so they stay fixed */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1 }}>
          <Tab label="TTS Testing" />
          <Tab label="STT Testing" />
          <Tab label="Samples" />
          <Tab label="Debug Logs" />
        </Tabs>
      </CardContent>

      {/* Scrollable content */}
      <Box className="panel-scroll">
        {/* TTS Testing Tab */}
        {tab === 0 && (
          <Box>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Voice Test
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Voice</InputLabel>
                    <Select
                      value={selectedVoice}
                      onChange={(e) => setSelectedVoice(e.target.value)}
                      label="Voice"
                    >
                      {Object.entries(VOICES).map(([key, voice]) => (
                        <MenuItem key={key} value={key}>
                          {voice.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Intonation</InputLabel>
                    <Select
                      value={selectedIntonation}
                      onChange={(e) => setSelectedIntonation(e.target.value)}
                      label="Intonation"
                    >
                      <MenuItem value="none">None</MenuItem>
                      {Object.keys(INTONATION_TAGS).map((key) => (
                        <MenuItem key={key} value={key}>
                          {key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleTestVoice}
                  startIcon={<PlayArrow />}
                  fullWidth
                >
                  Test Voice
                </Button>
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Full Narrative Test
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={ttsText}
                  onChange={(e) => setTtsText(e.target.value)}
                  placeholder="Enter narrative with voice tags..."
                  sx={{ mb: 2, fontFamily: "monospace", fontSize: "0.9rem" }}
                />
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handlePlayTts}
                    startIcon={<PlayArrow />}
                  >
                    Play
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleStopTts}
                    startIcon={<Stop />}
                  >
                    Stop
                  </Button>
                  <IconButton onClick={() => setMuted(!muted)}>
                    {muted ? <VolumeOff /> : <VolumeUp />}
                  </IconButton>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography gutterBottom>Playback Speed: {playbackSpeed.toFixed(2)}x</Typography>
                  <Slider
                    value={playbackSpeed}
                    onChange={(_, v) => {
                      setPlaybackSpeed(v as number);
                      playerRef.current?.setSpeed(v as number);
                    }}
                    min={0.5}
                    max={2}
                    step={0.05}
                  />
                </Box>
                {ttsStatus && (
                  <Alert severity="info">
                    {ttsStatus}
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Available Tags
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Voice Switching:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {["[NARRATOR]", "[COMPUTER]", "[PA]", "[NPC:pirate]", "[NPC:trader]", "[NPC:official]"].map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onClick={() => copyToClipboard(tag)}
                      icon={<ContentCopy fontSize="small" />}
                    />
                  ))}
                </Box>
                <Typography variant="subtitle2" gutterBottom>
                  Intonations:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Object.keys(INTONATION_TAGS).map((tag) => (
                    <Chip
                      key={tag}
                      label={`[${tag.toUpperCase()}]`}
                      size="small"
                      onClick={() => copyToClipboard(`[${tag.toUpperCase()}]...[/${tag.toUpperCase()}]`)}
                      icon={<ContentCopy fontSize="small" />}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* STT Testing Tab */}
        {tab === 1 && (
          <Box>
            {/* LIVE TRANSCRIPT - Very prominent */}
            {transcript && (
              <Paper
                sx={{
                  p: 3,
                  mb: 2,
                  bgcolor: "primary.dark",
                  border: 2,
                  borderColor: "primary.main",
                  minHeight: 120,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: "error.main",
                      animation: "pulse 1.5s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.3 },
                      },
                    }}
                  />
                  <Typography variant="h6" color="primary.light">
                    LISTENING - Transcript appears here as you speak:
                  </Typography>
                </Box>
                <Typography variant="h5" color="primary.contrastText" sx={{ fontWeight: 500 }}>
                  {transcript}
                </Typography>
              </Paper>
            )}

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Speech-to-Text Recording
                </Typography>
                <Button
                  variant={isRecording ? "contained" : "outlined"}
                  color={isRecording ? "error" : "primary"}
                  onClick={handleToggleRecording}
                  startIcon={<Mic />}
                  fullWidth
                  sx={{ mb: 2, fontSize: "1.1rem", py: 2 }}
                >
                  {isRecording ? "🔴 STOP RECORDING" : "▶ START RECORDING"}
                </Button>

                {isRecording && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" gutterBottom>
                      Volume Level
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={volume * 100}
                      sx={{ height: 12, borderRadius: 2 }}
                    />
                  </Box>
                )}

                {sttStatus && (
                  <Alert severity={sttStatus.includes("Error") ? "error" : "info"} sx={{ mb: 2 }}>
                    {sttStatus}
                  </Alert>
                )}

                {finalTranscript && (
                  <Paper sx={{ p: 2, bgcolor: "success.dark" }}>
                    <Typography variant="caption" color="success.light">
                      Final Transcript:
                    </Typography>
                    <Typography variant="body1" color="success.contrastText" sx={{ mt: 1 }}>
                      {finalTranscript}
                    </Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>

            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                <strong>How to use:</strong>
              </Typography>
              <Typography variant="body2">
                1. Tap "START RECORDING"<br/>
                2. Wait for green "ready" state<br/>
                3. Speak clearly - transcript appears above in real-time<br/>
                4. Tap "STOP RECORDING" when done
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Sample Narratives Tab */}
        {tab === 2 && (
          <Box>
            {sampleNarratives.map((sample, idx) => (
              <Card key={idx} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {sample.name}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={sample.text}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2, fontFamily: "monospace", fontSize: "0.85rem" }}
                  />
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (!playerRef.current) return;
                      setTtsStatus("Loading sample...");
                      addLog("request", `Playing sample: ${sample.name}`, { text: sample.text });
                      await playerRef.current.load(sample.text);
                      await playerRef.current.play();
                    }}
                    startIcon={<PlayArrow />}
                    fullWidth
                  >
                    Play Sample
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Debug Logs Tab */}
        {tab === 3 && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6">Debug Logs</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Delete />}
                onClick={clearLogs}
              >
                Clear Logs
              </Button>
            </Box>
            <Paper
              ref={debugBoxRef}
              sx={{
                p: 2,
                bgcolor: "grey.900",
                color: "grey.100",
                fontFamily: "monospace",
                fontSize: "0.85rem",
                maxHeight: "70vh",
                overflow: "auto",
              }}
            >
              {debugLogs.length === 0 && (
                <Typography color="grey.500">No logs yet...</Typography>
              )}
              {debugLogs.map((log, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  <Typography
                    component="span"
                    sx={{
                      color:
                        log.type === "error"
                          ? "error.main"
                          : log.type === "request"
                          ? "info.main"
                          : log.type === "response"
                          ? "success.main"
                          : "grey.400",
                    }}
                  >
                    [{log.timestamp}] [{log.type.toUpperCase()}]
                  </Typography>{" "}
                  <Typography component="span" sx={{ color: "grey.100" }}>
                    {log.message}
                  </Typography>
                  {log.data && (
                    <Box
                      sx={{
                        mt: 0.5,
                        pl: 2,
                        color: "grey.400",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {JSON.stringify(log.data, null, 2)}
                    </Box>
                  )}
                  <Divider sx={{ my: 1, borderColor: "grey.800" }} />
                </Box>
              ))}
            </Paper>
          </Box>
        )}
      </Box>
    </Card>
  );
}
